import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articleMap, getRelatedArticles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';
import { useEffect } from 'react';

const articleImageUrls: Record<string, string> = {
  'article-01': 'https://readdy.ai/api/search-image?query=classic%20Hong%20Kong%20cinema%20government%20building%20with%20film%20reels%2C%20vintage%20legal%20documents%2C%20warm%20golden%20lighting%2C%20academic%20library%20atmosphere%2C%20cinematic%20composition&width=1200&height=600&seq=article-lg-01&orientation=landscape',
  'article-02': 'https://readdy.ai/api/search-image?query=global%20film%20festival%20awards%20ceremony%20backdrop%2C%20world%20cinema%20comparison%2C%20warm%20spotlight%2C%20elegant%20theater%20setting%2C%20golden%20statues&width=1200&height=600&seq=article-lg-02&orientation=landscape',
  'article-03': 'https://readdy.ai/api/search-image?query=artistic%20film%20production%20set%20with%20dramatic%20cinematic%20lighting%2C%20director%20chair%2C%20camera%2C%20warm%20golden%20backlight%2C%20film%20aesthetic&width=1200&height=600&seq=article-lg-03&orientation=landscape',
  'article-04': 'https://readdy.ai/api/search-image?query=Taiwan%20traditional%20theater%20with%20modern%20cinema%20elements%2C%20warm%20lantern%20lighting%2C%20film%20posters%2C%20cultural%20atmosphere&width=1200&height=600&seq=article-lg-04&orientation=landscape',
  'article-05': 'https://readdy.ai/api/search-image?query=legal%20library%20with%20film%20censorship%20documents%2C%20gavel%20and%20film%20reel%2C%20warm%20wooden%20interior%2C%20scholarly%20atmosphere&width=1200&height=600&seq=article-lg-05&orientation=landscape',
  'article-06': 'https://readdy.ai/api/search-image?query=1990s%20Hong%20Kong%20neon%20street%20with%20cinema%20marquee%2C%20vintage%20movie%20theater%2C%20warm%20nostalgic%20lighting%2C%20golden%20era%20aesthetic&width=1200&height=600&seq=article-lg-06&orientation=landscape',
  'article-07': 'https://readdy.ai/api/search-image?query=sociology%20research%20concept%20with%20magnifying%20glass%20over%20film%20strip%2C%20books%2C%20warm%20academic%20lighting%2C%20cultural%20studies&width=1200&height=600&seq=article-lg-07&orientation=landscape',
  'article-08': 'https://readdy.ai/api/search-image?query=Japanese%20traditional%20theater%20stage%20with%20modern%20projector%2C%20warm%20paper%20lantern%20lighting%2C%20cross%20cultural%20film%20elements&width=1200&height=600&seq=article-lg-08&orientation=landscape',
  'article-09': 'https://readdy.ai/api/search-image?query=digital%20tablet%20beside%20vintage%20film%20projector%2C%20warm%20transition%20lighting%2C%20old%20and%20new%20cinema%20technology%2C%20elegant%20composition&width=1200&height=600&seq=article-lg-09&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=Korean%20film%20award%20ceremony%20with%20red%20carpet%2C%20golden%20trophy%2C%20warm%20cinematic%20lighting%2C%20elegant%20atmosphere&width=1200&height=600&seq=article-lg-10&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=official%20film%20approval%20stamp%20on%20documents%2C%20Chinese%20cinema%20theme%2C%20warm%20office%20lighting%2C%20professional%20setting&width=1200&height=600&seq=article-lg-11&orientation=landscape',
  'article-12': 'https://readdy.ai/api/search-image?query=camera%20lens%20with%20color%20palette%20and%20film%20strip%2C%20artistic%20photography%20studio%2C%20warm%20creative%20lighting%2C%20visual%20aesthetics&width=1200&height=600&seq=article-lg-12&orientation=landscape',
};

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleMap[slug] : undefined;
  const relatedArticles = slug ? getRelatedArticles(slug, 3) : [];

  useEffect(() => {
    if (article && slug) {
      document.title = `${article.title} - 电影分级研究`;
    }
  }, [article, slug]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-6xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-semibold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categoryMap[article.category];
  const imgUrl = articleImageUrls[article.imageSeq] || articleImageUrls['article-01'];

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="max-w-6xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: category?.name || article.category, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-10 pb-14 md:pb-20">
            <div className="flex-1 min-w-0">
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-3 py-1 bg-primary-100/70 text-primary-500 text-xs font-medium rounded-full whitespace-nowrap cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500">{article.author}</span>
                  <span className="text-xs text-foreground-400">·</span>
                  <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-base text-foreground-600 leading-relaxed">
                  {article.excerpt}
                </p>
              </header>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={imgUrl}
                  alt={article.title}
                  className="w-full h-auto"
                  title={article.title}
                />
              </div>

              <div className="prose-custom">
                {article.content.map((paragraph, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-lg font-semibold text-foreground-950 mb-5">相关文章</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.slug} article={ra} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-20 space-y-6">
                <div className="bg-background-100 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-3">文章信息</h4>
                  <div className="space-y-2 text-sm text-foreground-600">
                    <div className="flex justify-between">
                      <span>作者</span>
                      <span className="font-medium text-foreground-950">{article.author}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>发布日期</span>
                      <span>{article.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>阅读时长</span>
                      <span>{article.readTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>所属分类</span>
                      {category && (
                        <Link
                          to={`/category/${category.slug}`}
                          className="text-primary-500 hover:text-primary-600 cursor-pointer"
                        >
                          {category.name}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-5">
                  <h4 className="text-sm font-semibold text-foreground-950 mb-3">热门分类</h4>
                  <div className="space-y-2">
                    {categories.slice(0, 5).map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className={`${cat.icon} text-base`}></i>
                        <span>{cat.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}